"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ChangeBillingAccountDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  /** Member who will become the billing account. */
  newBillingName: string;
  /** Member who is the billing account today, when known. */
  currentBillingName?: string;
  isLoading?: boolean;
}

export function ChangeBillingAccountDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  newBillingName,
  currentBillingName,
  isLoading = false,
}: ChangeBillingAccountDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Change billing account?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-muted-foreground text-sm">
              <p>
                The billing account is the single organization member whose
                Stripe customer is charged. There is exactly one per
                organization, so making <strong>{newBillingName}</strong> the
                billing account
                {currentBillingName ? (
                  <>
                    {" "}
                    removes it from <strong>{currentBillingName}</strong>
                  </>
                ) : null}
                .
              </p>
              <p>After this change:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  Card and ACH checkout, and the Stripe billing portal, will use{" "}
                  <strong>{newBillingName}</strong>&rsquo;s Stripe customer.
                </li>
                <li>
                  Saved cards and bank accounts <strong>do not move</strong>.
                  Any payment method saved by{" "}
                  {currentBillingName ?? "the current billing account"} stays on
                  their Stripe customer and will not be available at checkout.
                  Switching back restores them.
                </li>
                <li>
                  Past payments and receipts are unchanged and stay visible in
                  payment history.
                </li>
                <li>
                  A checkout already in progress keeps using the previous
                  billing account. Cancel and start it again to use the new one.
                </li>
              </ul>
              <p>
                This does not change who is allowed to make payments, or who
                receives invoice emails.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "Changing..." : "Change billing account"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
